import React, { useState, useEffect, useCallback } from "react";
import "../../day-plan-styles/TaskCard.css";
import CollapsedTaskCard from "./CollapsedTaskCard";
import ExpandedTaskCard from "./ExpandedTaskCard";
import { PlanData, HourlyTaskCard, DailyTaskCard, CustomTaskCard } from "../../../../types/types";
import { hasProducedInput, hasAvailableInput } from "../../../../utils/planDataHelpers";

interface TaskCardProps {
  planData: PlanData;
  currentStatus: string;
  onHourlyStatusChange?: (card: HourlyTaskCard, from: string, to: string) => void;
  onDailyStatusChange?: (card: DailyTaskCard, from: string, to: string) => void;
  onCustomStatusChange?: (card: CustomTaskCard, from: string, to: string) => void;
  hourlyCard?: HourlyTaskCard;
  dailyCard?: DailyTaskCard;
  customCard?: CustomTaskCard;
  isExpanded: boolean;
  onToggle: () => void;
  isEditable: boolean;
  updateHour: string;
  onViewBreakdown?: (planData: PlanData) => void;
}

function TaskCard({ planData, currentStatus, onHourlyStatusChange, onDailyStatusChange, onCustomStatusChange, hourlyCard, dailyCard, customCard, isExpanded, onToggle, isEditable, updateHour, onViewBreakdown }: TaskCardProps) {
  const aggregateCard = hourlyCard || dailyCard || customCard;
  const showAvailable = hasAvailableInput(planData);
  const showProduced = hasProducedInput(planData);
  const [liveAvailable, setLiveAvailable] = useState<number>(showAvailable ? (aggregateCard?.itemSalesPlan.availableVol || 0) : 0);
  const [liveProduced, setLiveProduced] = useState<number>(showProduced ? (aggregateCard?.itemSalesPlan.producedVol || 0) : 0);

  useEffect(() => {
    if (aggregateCard) {
      setLiveAvailable(showAvailable ? (aggregateCard.itemSalesPlan.availableVol || 0) : 0);
      setLiveProduced(showProduced ? (aggregateCard.itemSalesPlan.producedVol || 0) : 0);
    }
  }, [aggregateCard?.itemSalesPlan.availableVol, aggregateCard?.itemSalesPlan.producedVol, showAvailable, showProduced]);

  const handleValuesChange = useCallback((available: number, produced: number) => {
    setLiveAvailable(available);
    setLiveProduced(produced);
  }, []);

  if (isExpanded) {
    return (
      <div className="task-card-wrapper-expanded">
        <CollapsedTaskCard 
          planData={planData}
          currentStatus={currentStatus} 
          onHourlyStatusChange={onHourlyStatusChange}
          onDailyStatusChange={onDailyStatusChange}
          onCustomStatusChange={onCustomStatusChange}
          hourlyCard={hourlyCard}
          dailyCard={dailyCard}
          customCard={customCard}
          onToggle={onToggle}
          liveAvailable={liveAvailable}
          liveProduced={liveProduced}
        />
        <ExpandedTaskCard 
          planData={planData} 
          onToggle={onToggle}
          isEditable={isEditable}
          updateHour={updateHour}
          currentStatus={currentStatus}
          dailyCard={dailyCard}
          hourlyCard={hourlyCard}
          customCard={customCard}
          onValuesChange={handleValuesChange}
          onViewBreakdown={onViewBreakdown}
        />
      </div>
    );
  } else {
    return (
      <div className="task-card-wrapper-collapsed">
        <CollapsedTaskCard 
          planData={planData}
          currentStatus={currentStatus} 
          onHourlyStatusChange={onHourlyStatusChange}
          onDailyStatusChange={onDailyStatusChange}
          onCustomStatusChange={onCustomStatusChange}
          hourlyCard={hourlyCard}
          dailyCard={dailyCard}
          customCard={customCard}
          onToggle={onToggle}
          liveAvailable={liveAvailable}
          liveProduced={liveProduced}
        />
      </div>
    );
  }
}

export default TaskCard;
